import { Urls } from '@/constants/urls';
import envService from '@/services/env.service';
import { handleExternalLink } from '@/utils/link-props';
import { Box, Flex, FlexProps, Link, Image, Text, Divider } from '@cmpsr/components';
import React from 'react';

function FooterDashboard(props: FlexProps) {
  return (
    <Box
      as="footer"
      background="url(/images/footer-dashboard_3840px.svg) repeat-x top center"
      pt="200px"
      mt={12}
      {...props}
    >
      <Box
        position="relative"
        top="-1px"
        width="100%"
        height="100%"
        background="gradient-02"
        backgroundSize="3840px"
        bgPosition="center"
        pt="spacer-8"
        pb="spacer-12"
        px={{ base: 'spacer-4', md: 'spacer-8', lg: 'spacer-16', xl: 'spacer-24', xxl: 'spacer-32' }}
      >
        <Flex flexDir="column" maxW="1184px" mx="auto">
          <Flex
            flexDir={{ base: 'column', lg: 'row' }}
            alignItems="flex-start"
            gap={{ base: 'spacer-12', lg: 'spacer-40' }}
          >
            <Flex width="auto" alignItems="center" justifyContent="flex-start" gap={0}>
              <Image src="/images/Catch-logo-footer.svg" width="112px" height="32px" alt="Footer Dashboard" />
              <Text
                as="span"
                display="inline-block"
                mt={{ base: '-20px', lg: '-25px' }}
                color="white"
                fontSize="12px"
                fontWeight="400"
              >
                beta
              </Text>
            </Flex>

            <Flex flexDir="row" flexWrap={{ base: 'wrap', md: 'nowrap' }} gap={{ base: 'spacer-12', md: 'spacer-6' }}>
              <Flex
                flexDir="column"
                gap="spacer-5"
                width={{ base: '144px', md: '219px', lg: '203px', xl: '256px', xxl: '288px' }}
              >
                <Text variant="text-body-bold" color="text-light">
                  About Us
                </Text>
                <Flex flexDir="column" gap="spacer-3">
                  <Link
                    target="_blank"
                    {...handleExternalLink({ href: Urls.how_it_works })}
                    aria-label="Navigate to how it works"
                    variant="link-accent"
                    size="m"
                  >
                    How it Works
                  </Link>
                  <Link
                    target="_blank"
                    {...handleExternalLink({ href: Urls.our_mission })}
                    aria-label="Navigate to our mission"
                    variant="link-accent"
                    size="m"
                  >
                    Our Mission
                  </Link>
                  <Link
                    target="_blank"
                    {...handleExternalLink({ href: Urls.the_science })}
                    aria-label="Navigate to the science"
                    variant="link-accent"
                    size="m"
                  >
                    The Science
                  </Link>
                </Flex>
              </Flex>
              <Flex
                flexDir="column"
                gap="spacer-5"
                width={{ base: '144px', md: '219px', lg: '203px', xl: '256px', xxl: '288px' }}
              >
                <Text variant="text-body-bold" color="text-light">
                  Legal
                </Text>
                <Flex flexDir="column" gap="spacer-3">
                  <Link
                    target="_blank"
                    {...handleExternalLink({ href: Urls.terms_of_service })}
                    aria-label="Navigate to terms of service"
                    variant="link-accent"
                    size="m"
                  >
                    Terms of Service
                  </Link>
                  <Link
                    target="_blank"
                    {...handleExternalLink({ href: Urls.privacy_policy })}
                    aria-label="Navigate to privacy policy"
                    variant="link-accent"
                    size="m"
                  >
                    Privacy Policy
                  </Link>
                </Flex>
              </Flex>
              <Flex
                flexDir="column"
                gap="spacer-5"
                width={{ base: '144px', md: '219px', lg: '203px', xl: '256px', xxl: '288px' }}
              >
                <Text variant="text-body-bold" color="text-light">
                  Support
                </Text>
                <Flex flexDir="column" gap="spacer-3">
                  <Link
                    target="_blank"
                    {...handleExternalLink({ href: envService.getEnv().VITE_SUPPORT_APP })}
                    aria-label="Access to contact email"
                    variant="link-accent"
                    size="m"
                  >
                    Contact Us
                  </Link>
                  <Link
                    target="_blank"
                    {...handleExternalLink({ href: Urls.faqs })}
                    aria-label="Navigate to FAQs"
                    variant="link-accent"
                    size="m"
                  >
                    FAQs
                  </Link>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
          <Divider my={{ base: 'spacer-10', xxl: 'spacer-12' }} />
          <Flex flexDir="column" gap="spacer-8">
            <Box>
              <Text variant="text-body-meta-regular" color="text-secondary-negative">
                This site is offered for informational purposes only, and is not intended to be used as medical advice,
                or for diagnosis or treatment purposes.
              </Text>
              <Text variant="text-body-meta-regular" color="text-secondary-negative">
                Copyright © 2025 Catch Bio, Inc. - All Rights Reserved
              </Text>
            </Box>
            <Flex gap="spacer-4">
              <Link
                target="_blank"
                {...handleExternalLink({ href: 'https://instagram.com/catchbio/' })}
                aria-label="Navigate to instagram"
              >
                <Image src="/images/instagram.svg" height="18px" width="18px" alt="Instagram logo" />
              </Link>
              <Link
                target="_blank"
                {...handleExternalLink({ href: 'https://x.com/Catch_Bio' })}
                aria-label="Navigate to X social network"
              >
                <Image src="/images/x.svg" height="18px" width="17.2px" alt="X logo" />
              </Link>
              <Link
                target="_blank"
                {...handleExternalLink({ href: 'https://linkedin.com/company/catchlabs/' })}
                aria-label="Navigate to linkedIn"
              >
                <Image src="/images/linkedIn.svg" height="18px" width="18px" alt="linkedIn logo" />
              </Link>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}

export default React.memo(FooterDashboard);
